// src/components/BonkCreationForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Rocket, Info, ExternalLink, AlertCircle, TrendingUp, Droplet, Zap, Shield, 
  DollarSign, Settings, Clock, Lock, Unlock, Package, Eye, EyeOff,
  Check, CheckCircle
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import { Image as ImageIcon, Loader2, X } from "lucide-react";
import { apiEndpoints } from '../services/api.service';
import { bundlerService } from '../services/frontend-bundler/bundler.service';
import Modal from './Modal'; // Import the Modal component

interface TokenFormData {
  name: string;
  ticker: string;
  description: string;
  imageUrl: string;
  website: string;
  twitter: string;
  telegram: string;
  discord: string;
}

interface BonkLaunchSettings {
  launchMode: 'JustSendIt' | 'LaunchLab';
  totalSupply: number;
  solToRaise: number;
  sellPercentage: number;
  curveType: 'linear' | 'exponential' | 'logarithmic';
  vestingEnabled: boolean;
  vestingPercentage: number;
  cliffPeriod: number; // in days
  unlockPeriod: number; // in days
  migrateType: 'amm' | 'cpmm';
  enableCreatorFees: boolean;
  platformFeeRate: number;
  slippage: number;
  priorityFee: number;
  useJitoBundle: boolean;
  jitoTip: number;
}

const BonkCreationForm: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'funding' | 'creating' | 'success'>('form');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [fundingWallet, setFundingWallet] = useState<string>('');
  const [amountToFund, setAmountToFund] = useState<number>(0);
  const [createdTokenAddress, setCreatedTokenAddress] = useState<string>('');
  const [txHash, setTxHash] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const MAX_BYTES = 5 * 1024 * 1024;
  
  const [formData, setFormData] = useState<TokenFormData>({
    name: '',
    ticker: '',
    description: '',
    imageUrl: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: ''
  });

  const [launchSettings, setLaunchSettings] = useState<BonkLaunchSettings>({
    launchMode: 'JustSendIt',
    totalSupply: 1000000000, // 1B tokens (standard)
    solToRaise: 85, // Default for JustSendIt
    sellPercentage: 80, // 80% sold on curve
    curveType: 'exponential',
    vestingEnabled: false,
    vestingPercentage: 0,
    cliffPeriod: 0,
    unlockPeriod: 0,
    migrateType: 'cpmm',
    enableCreatorFees: true,
    platformFeeRate: 100, // 1% in bps
    slippage: 10,
    priorityFee: 0.0005,
    useJitoBundle: false,
    jitoTip: 0.01
  });

  const [initialBuyAmount, setInitialBuyAmount] = useState<number>(0.1);

  // Bundle state (using real bundlerService)
  const [bundles, setBundles] = useState<any[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<any>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [bundlePassword, setBundlePassword] = useState('');
  const [showBundlePasswordModal, setShowBundlePasswordModal] = useState(false);
  const [bundleBuyProgress, setBundleBuyProgress] = useState<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
    status: string;
  }>({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });

  // Load bundles when wallet connects
  useEffect(() => {
    if (isConnected && publicKey) {
      loadUserBundles();
    }
  }, [isConnected, publicKey]);

  const loadUserBundles = async () => {
    if (!publicKey) return;
    try {
      const userBundles = await bundlerService.getUserBundles(publicKey.toString());
      setBundles(userBundles);
      
      // Check if there's an active unlocked bundle
      const currentBundle = bundlerService.getCurrentBundle();
      if (currentBundle && !bundlerService.getIsLocked()) {
        setSelectedBundle(currentBundle);
        setIsUnlocked(true);
      }
    } catch (error) {
      console.error('Failed to load bundles:', error);
    }
  };

  const unlockSelectedBundle = async () => {
    if (!selectedBundle || !bundlePassword) return;
    
    try {
      const unlockedBundle = await bundlerService.unlockBundle(
        selectedBundle.id, 
        bundlePassword
      );
      setIsUnlocked(true);
      setShowBundlePasswordModal(false);
      setBundlePassword('');
      console.log(`✅ Bundle "${unlockedBundle.name}" unlocked successfully!`);
    } catch (error: any) {
      alert(`Failed to unlock bundle: ${error.message}`);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      alert('Only PNG, JPG, GIF, WEBP are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      alert(`File is too large (max ${(MAX_BYTES/1024/1024).toFixed(0)}MB)`);
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${apiEndpoints.uploadLogo}?folder=bonk-tokens&prefix=bonk`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
      
      const data = await response.json();
      
      const imageUrl = data.secure_url || data.url || data.imageUrl;
      
      if (!imageUrl) {
        throw new Error('No image URL in response');
      }
      
      setFormData(prev => ({ ...prev, imageUrl }));
      
      console.log('✅ Image uploaded successfully:', imageUrl);
      
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Fallback to base64 encoding if upload fails
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          setFormData(prev => ({ ...prev, imageUrl: base64 }));
          console.log('📦 Using base64 fallback');
        };
        reader.readAsDataURL(file);
      } catch (fallbackError) {
        alert('Failed to upload image. Please try again.');
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name || formData.name.length > 50) {
      alert('Token name is required and must be less than 50 characters');
      return false;
    }
    if (!formData.ticker || formData.ticker.length > 10) {
      alert('Token ticker is required and must be less than 10 characters');
      return false;
    }
    if (!formData.description || formData.description.length > 500) {
      alert('Description is required and must be less than 500 characters');
      return false;
    }
    if (!formData.imageUrl) {
      alert('Token logo is required');
      return false;
    }
    if (!isConnected || !publicKey) {
      alert('Please connect your wallet first');
      return false;
    }

    // Bonk-specific validations
    if (launchSettings.launchMode === 'LaunchLab') {
      if (launchSettings.solToRaise < 30) {
        alert('Minimum SOL to raise is 30 for LaunchLab mode');
        return false;
      }
      if (launchSettings.sellPercentage < 20 || launchSettings.sellPercentage > 80) {
        alert('Sell percentage must be between 20% and 80%');
        return false;
      }
      if (launchSettings.vestingEnabled && launchSettings.vestingPercentage > 30) {
        alert('Maximum vesting percentage is 30%');
        return false;
      }
    }

    return true;
  };

  const calculateTotalCost = (): number => {
    let baseCost = 0.01; // Base LetsBonk.fun creation cost
    
    // If bundle is selected and unlocked, initial buy comes from bundle wallets
    if (selectedBundle && isUnlocked) {
      // No additional cost from main wallet for initial buy
    } else if (initialBuyAmount > 0) {
      baseCost += initialBuyAmount;
    }
    
    if (launchSettings.useJitoBundle) {
      baseCost += launchSettings.jitoTip;
    }

    return baseCost;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setBundleBuyProgress({
      total: 0,
      completed: 0,
      successful: 0,
      failed: 0,
      status: 'Preparing token launch...'
    });
    
    try {
      // Check for Jito bundle (not implemented)
      if (launchSettings.useJitoBundle) {
        throw new Error(
          'Jito bundle flow is not implemented yet. Please disable MEV protection or implement Jito submission.'
        );
      }

      setStep('funding');
      
      // Step 1: Prepare Bonk token creation
      const prepareData = {
        name: formData.name,
        ticker: formData.ticker,
        description: formData.description,
        imageUrl: formData.imageUrl,
        website: formData.website,
        twitter: formData.twitter,
        telegram: formData.telegram,
        discord: formData.discord,
        launchMode: launchSettings.launchMode,
        totalSupply: launchSettings.totalSupply,
        solToRaise: launchSettings.solToRaise,
        sellPercentage: launchSettings.sellPercentage,
        curveType: launchSettings.curveType,
        vestingEnabled: launchSettings.vestingEnabled,
        vestingPercentage: launchSettings.vestingPercentage,
        cliffPeriod: launchSettings.cliffPeriod,
        unlockPeriod: launchSettings.unlockPeriod,
        migrateType: launchSettings.migrateType,
        enableCreatorFees: launchSettings.enableCreatorFees,
        platformFeeRate: launchSettings.platformFeeRate,
        initialBuyAmount: selectedBundle && isUnlocked ? 0 : initialBuyAmount,
        developerWallet: publicKey!.toString()
      };

      // Call your Bonk creation service
      const prepareResult = await fetch('/api/bonk/prepare-launch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prepareData)
      });

      if (!prepareResult.ok) {
        throw new Error('Failed to prepare Bonk token creation');
      }

      const prepareResponse = await prepareResult.json();
      setRequestId(prepareResponse.request_id);
      setFundingWallet(prepareResponse.funding_wallet);
      setAmountToFund(prepareResponse.amount_to_fund);

      // Step 2: Fund the creation wallet
      const fundingSuccess = await fundCreationWallet(
        prepareResponse.funding_wallet,
        prepareResponse.amount_to_fund
      );

      if (!fundingSuccess) {
        throw new Error('Failed to fund creation wallet');
      }

      // Step 3: Create the token on LetsBonk.fun
      setStep('creating');
      setBundleBuyProgress(prev => ({ ...prev, status: 'Creating token on LetsBonk.fun...' }));
      
      const createResult = await fetch('/api/bonk/create-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: prepareResponse.request_id,
          ...prepareData
        })
      });

      if (!createResult.ok) {
        throw new Error('Failed to create token on LetsBonk.fun');
      }

      const createResponse = await createResult.json();
      const mintAddress = createResponse.tokenAddress || '';
      
      // Step 4: Execute bundle purchases if a bundle is selected and unlocked
      if (selectedBundle && isUnlocked && mintAddress && initialBuyAmount > 0) {
        console.log('🎯 Starting bundle buys for token:', mintAddress);

        setBundleBuyProgress({
          total: selectedBundle.wallets.length,
          completed: 0,
          successful: 0,
          failed: 0,
          status: 'Executing bundle purchases...',
        });

        const bundleResult = await bundlerService.executeBundlePurchase(
          mintAddress,
          initialBuyAmount,
          launchSettings.slippage,
          (progress, status) => {
            const match = status.match(/(\d+) success, (\d+) failed/);
            if (match) {
              setBundleBuyProgress({
                total: selectedBundle.wallets.length,
                completed: parseInt(match[1]) + parseInt(match[2]),
                successful: parseInt(match[1]),
                failed: parseInt(match[2]),
                status,
              });
            } else {
              setBundleBuyProgress((prev) => ({ ...prev, status }));
            }
          }
        );

        if (bundleResult.success) {
          console.log(`✅ Bundle buys complete: ${bundleResult.successful} successful`);
          
          setBundleBuyProgress((prev) => ({
            ...prev,
            completed: prev.total,
            successful: bundleResult.successful,
            failed: bundleResult.failed,
            status: `Bundle purchases complete! ${bundleResult.successful}/${prev.total} successful`,
          }));
        } else {
          console.error('Bundle buy failed:', bundleResult.error);
        }
      }
      
      setCreatedTokenAddress(mintAddress);
      setStep('success');

    } catch (error: any) {
      console.error('Failed to create Bonk token:', error);
      alert(`Failed to create token: ${error.message}`);
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const fundCreationWallet = async (
    fundingAddress: string, 
    amount: number
  ): Promise<boolean> => {
    try {
      if (!publicKey) {
        throw new Error('Wallet not connected');
      }

      console.log(`💰 Funding wallet ${fundingAddress} with ${amount} SOL...`);

      const connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
      const fromPubkey = new PublicKey(publicKey);
      const toPubkey = new PublicKey(fundingAddress);

      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey,
          toPubkey,
          lamports: amount * LAMPORTS_PER_SOL
        })
      );

      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;

      const provider = (window as any).solana;
      if (!provider?.isPhantom) {
        throw new Error('Phantom wallet not found');
      }

      const signedTransaction = await provider.signTransaction(transaction);
      const signature = await connection.sendRawTransaction(signedTransaction.serialize());
      
      await connection.confirmTransaction(signature, 'confirmed');
      
      console.log('✅ Funding transaction confirmed:', signature);
      setTxHash(signature);
      
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      return true;
    } catch (error: any) {
      console.error('❌ Failed to fund wallet:', error);
      alert(`Failed to fund wallet: ${error.message}`);
      return false;
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ticker: '',
      description: '',
      imageUrl: '',
      website: '',
      twitter: '',
      telegram: '',
      discord: ''
    });
    setLaunchSettings({
      launchMode: 'JustSendIt',
      totalSupply: 1000000000,
      solToRaise: 85,
      sellPercentage: 80,
      curveType: 'exponential',
      vestingEnabled: false,
      vestingPercentage: 0,
      cliffPeriod: 0,
      unlockPeriod: 0,
      migrateType: 'cpmm',
      enableCreatorFees: true,
      platformFeeRate: 100,
      slippage: 10,
      priorityFee: 0.0005,
      useJitoBundle: false,
      jitoTip: 0.01
    });
    setInitialBuyAmount(0.1);
    setStep('form');
    setRequestId(null);
    setFundingWallet('');
    setAmountToFund(0);
    setCreatedTokenAddress('');
    setTxHash('');
    setShowAdvanced(false);
    setSelectedBundle(null);
    setIsUnlocked(false);
    setBundleBuyProgress({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });
  };

  // Success screen
  if (step === 'success') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-orange-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Token Launched on LetsBonk.fun!</h2>
          <p className="text-gray-400 mb-6">Your token is now live and trading on the bonding curve</p>
          
          <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-400 mb-1">Token Address</div>
            <div className="font-mono text-sm break-all">{createdTokenAddress}</div>
          </div>

          {txHash && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-400 mb-1">Transaction Hash</div>
              <div className="font-mono text-xs break-all">{txHash}</div>
            </div>
          )}

          {bundleBuyProgress.total > 0 && (
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4 mb-6">
              <div className="text-sm font-medium text-purple-400 mb-2">Bundle Purchase Results</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-gray-900/50 rounded p-2">
                  <div className="text-xs text-gray-500">Total</div>
                  <div className="font-semibold">{bundleBuyProgress.total}</div>
                </div>
                <div className="bg-green-900/30 rounded p-2">
                  <div className="text-xs text-gray-500">Successful</div>
                  <div className="font-semibold text-green-400">{bundleBuyProgress.successful}</div>
                </div>
                <div className="bg-red-900/30 rounded p-2">
                  <div className="text-xs text-gray-500">Failed</div>
                  <div className="font-semibold text-red-400">{bundleBuyProgress.failed}</div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <a
              href={`https://letsbonk.fun/token/${createdTokenAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View on LetsBonk</span>
            </a>
            <a
              href={`https://solscan.io/token/${createdTokenAddress}${SOLANA_RPC_ENDPOINT.includes('devnet') ? '?cluster=devnet' : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View on Solscan</span>
            </a>
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
            >
              Launch Another
            </button>
          </div>

          <div className="text-xs text-gray-500">
            Your token will migrate to Raydium when the bonding curve reaches {launchSettings.solToRaise} SOL
          </div>
        </div>
      </div>
    );
  }

  // Loading screens
  if (step === 'funding' || step === 'creating') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-orange-400" />
          <h2 className="text-xl font-bold mb-2">
            {step === 'funding' ? 'Processing Payment...' : 'Launching on LetsBonk.fun...'}
          </h2>
          
          {step === 'funding' && fundingWallet && (
            <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
              <div className="text-sm text-gray-400 mb-2">Funding Wallet</div>
              <div className="font-mono text-xs break-all mb-2">{fundingWallet}</div>
              <div className="text-sm text-gray-400">Total Amount: {amountToFund} SOL</div>
              {initialBuyAmount > 0 && !selectedBundle && (
                <div className="text-xs text-gray-500 mt-1">
                  (Includes {initialBuyAmount} SOL for initial buy)
                </div>
              )}
            </div>
          )}
          
          {step === 'creating' && bundleBuyProgress.total > 0 && (
            <div className="mt-6 max-w-md mx-auto">
              <div className="text-sm font-medium mb-2">Bundle Buy Progress</div>
              <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
                <div 
                  className="bg-gradient-to-r from-orange-600 to-yellow-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(bundleBuyProgress.completed / bundleBuyProgress.total) * 100}%` }}
                />
              </div>
              <div className="text-xs text-gray-400">
                {bundleBuyProgress.completed}/{bundleBuyProgress.total} wallets • 
                <span className="text-green-400"> {bundleBuyProgress.successful} successful</span> • 
                <span className="text-red-400"> {bundleBuyProgress.failed} failed</span>
              </div>
            </div>
          )}
          
          <p className="text-gray-400 mt-4">
            {bundleBuyProgress.status || (
              step === 'funding' 
                ? 'Please approve the transaction in your wallet...'
                : 'Deploying your token on LetsBonk.fun with Raydium LaunchLab...'
            )}
          </p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-600/20 rounded-lg">
            <TrendingUp className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Launch on LetsBonk.fun</h2>
            <p className="text-sm text-gray-400">Community-driven launchpad powered by Raydium</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-xs text-gray-500">Platform Fee</div>
          <div className="text-lg font-semibold text-orange-400">~0.01 SOL</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <TrendingUp className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-orange-400 mb-1">LetsBonk.fun Features</div>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>✓ Built on Raydium LaunchLab infrastructure</li>
              <li>✓ Bonding curve with automatic Raydium migration</li>
              <li>✓ 1% trading fee + revenue sharing</li>
              <li>✓ BONK token buybacks from platform fees</li>
              <li>✓ Instant Jupiter/AlphaScan integration</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Launch Mode Selection */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Launch Mode <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setLaunchSettings({ ...launchSettings, launchMode: 'JustSendIt', solToRaise: 85 })}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                launchSettings.launchMode === 'JustSendIt'
                  ? 'bg-orange-600/20 border-orange-500 shadow-lg shadow-orange-500/20'
                  : 'bg-gray-900/50 border-gray-700 hover:border-orange-600/50'
              }`}
            >
              <div className="font-semibold mb-2">JustSendIt Mode</div>
              <div className="text-xs text-gray-400 space-y-1">
                <div>• Quick launch with standard settings</div>
                <div>• 85 SOL bonding curve target</div>
                <div>• Fixed parameters for simplicity</div>
                <div>• Ideal for memecoins</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setLaunchSettings({ ...launchSettings, launchMode: 'LaunchLab' })}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                launchSettings.launchMode === 'LaunchLab'
                  ? 'bg-orange-600/20 border-orange-500 shadow-lg shadow-orange-500/20'
                  : 'bg-gray-900/50 border-gray-700 hover:border-orange-600/50'
              }`}
            >
              <div className="font-semibold mb-2">LaunchLab Mode</div>
              <div className="text-xs text-gray-400 space-y-1">
                <div>• Full customization available</div>
                <div>• Custom bonding curve settings</div>
                <div>• Vesting and tokenomics control</div>
                <div>• Advanced project features</div>
              </div>
            </button>
          </div>
        </div>

        {/* Basic Token Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Token Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, 50) })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="My Bonk Token"
              maxLength={50}
            />
            <div className="text-xs text-gray-500 mt-1">
              {formData.name.length}/50 characters
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Token Symbol <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.ticker}
              onChange={(e) => setFormData({ ...formData, ticker: e.target.value.slice(0, 10).toUpperCase() })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="BONK"
              maxLength={10}
            />
            <div className="text-xs text-gray-500 mt-1">
              {formData.ticker.length}/10 characters
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, 500) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
            placeholder="Describe your token and its purpose..."
            rows={3}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.description.length}/500 characters
          </div>
        </div>

        {/* Token Logo Upload */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Logo <span className="text-red-500">*</span>
          </label>
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={async (e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) await handleFileUpload(file);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`w-full min-h-[140px] rounded-xl border-2 border-dashed transition-all cursor-pointer ${
              isDragging
                ? 'border-orange-400 bg-orange-500/5'
                : 'border-gray-700 bg-gray-900/30 hover:border-orange-500/50'
            } flex items-center justify-center p-6`}
          >
            {!formData.imageUrl && !uploadingLogo && (
              <div className="text-center">
                <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">
                  Drop your logo here or click to upload
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF or WEBP (max 5MB)
                </p>
              </div>
            )}

            {uploadingLogo && (
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-orange-400" />
                <span className="text-sm">Uploading to IPFS...</span>
              </div>
            )}

            {formData.imageUrl && !uploadingLogo && (
              <div className="flex items-center gap-4">
                <img
                  src={formData.imageUrl}
                  alt="Token Logo"
                  className="w-20 h-20 rounded-lg object-cover border-2 border-orange-500/30"
                />
                <div className="text-left">
                  <p className="text-sm text-gray-400 mb-2">Logo uploaded</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, imageUrl: '' }));
                      }}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await handleFileUpload(file);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
        </div>

        {/* LaunchLab Advanced Settings - Same as before */}
        {launchSettings.launchMode === 'LaunchLab' && (
          <div className="border border-gray-700 rounded-lg p-4 space-y-4">
            {/* ... (Keep all the LaunchLab settings as they were) ... */}
          </div>
        )}

        {/* Social Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Website <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="https://..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Twitter <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.twitter}
              onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="@username"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Telegram <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.telegram}
              onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="t.me/groupname"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Discord <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.discord}
              onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
              placeholder="discord.gg/invite"
            />
          </div>
        </div>

        {/* Advanced Launch Settings */}
        <div className="border-t border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="font-medium flex items-center space-x-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span>Advanced Launch Settings</span>
            </span>
            <span className="text-gray-400">
              {showAdvanced ? '−' : '+'}
            </span>
          </button>
          
          {showAdvanced && (
            <div className="mt-4 space-y-4">
              {/* Bundle Selection */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="font-medium flex items-center space-x-2 mb-4">
                  <Package className="w-4 h-4 text-purple-400" />
                  <span>Bundle Auto-Buy (Optional)</span>
                  {selectedBundle && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      isUnlocked 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
                    </span>
                  )}
                </h3>
                
                {bundles.length > 0 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {bundles.map((bundle) => {
                        const eligibleWallets = bundle.wallets.filter(
                          (w: any) => w.balance >= (initialBuyAmount + 0.002)
                        );
                        
                        return (
                          <button
                            key={bundle.id}
                            type="button"
                            onClick={() => {
                              setSelectedBundle(bundle);
                              setIsUnlocked(false);
                            }}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedBundle?.id === bundle.id
                                ? 'border-orange-500 bg-orange-500/10'
                                : 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium">{bundle.name}</div>
                              {selectedBundle?.id === bundle.id && (
                                <Check className="w-4 h-4 text-orange-400" />
                              )}
                            </div>
                            <div className="text-xs text-gray-400 space-y-1">
                              <div>{bundle.wallets.length} wallets • {bundle.totalBalance.toFixed(3)} SOL</div>
                              <div className={eligibleWallets.length > 0 ? 'text-green-400' : 'text-red-400'}>
                                {eligibleWallets.length} can afford {initialBuyAmount} SOL + fees
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {selectedBundle && !isUnlocked && (
                      <button
                        type="button"
                        onClick={() => setShowBundlePasswordModal(true)}
                        className="w-full px-4 py-2 bg-orange-600/20 text-orange-400 rounded-lg hover:bg-orange-600/30 transition-all"
                      >
                        Unlock Bundle for Auto-Buy
                      </button>
                    )}
                    
                    {selectedBundle && isUnlocked && (
                      <div className="p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium text-green-400">Bundle Ready</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {selectedBundle.wallets.filter((w: any) => 
                                w.balance >= (initialBuyAmount + 0.002)
                              ).length} wallets will automatically buy after token creation
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-400">
                    No bundles available. Create a bundle in Advanced Tools to enable auto-buying.
                  </div>
                )}
              </div>

              {/* Initial Buy */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Buy Amount per Wallet (SOL)
                </label>
                <input
                  type="number"
                  value={initialBuyAmount}
                  onChange={(e) => setInitialBuyAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500"
                  placeholder="0.1"
                  min="0"
                  step="0.01"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {selectedBundle && isUnlocked 
                    ? `Each wallet in bundle will buy this amount`
                    : `Amount to buy immediately after creation`}
                </div>
              </div>

              {/* Trading Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Slippage (%)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.slippage}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      slippage: Number(e.target.value)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500"
                    min="1"
                    max="50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Priority Fee (SOL)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.priorityFee}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      priorityFee: Number(e.target.value)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500"
                    min="0"
                    step="0.0001"
                  />
                </div>
              </div>

              {/* Jito Bundle Option */}
              <div className="bg-purple-900/10 border border-purple-700/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="jito-bundle"
                    checked={launchSettings.useJitoBundle}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      useJitoBundle: e.target.checked
                    })}
                    className="mt-1 w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="jito-bundle" className="flex-1 cursor-pointer">
                    <div className="font-medium flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span>MEV Protection (Jito Bundle)</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Protect against sandwich attacks and ensure transaction success
                    </p>
                  </label>
                </div>
                
                {launchSettings.useJitoBundle && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium mb-2">
                      Jito Tip (SOL)
                    </label>
                    <input
                      type="number"
                      value={launchSettings.jitoTip}
                      onChange={(e) => setLaunchSettings({
                        ...launchSettings,
                        jitoTip: Number(e.target.value)
                      })}
                      className="w-full px-3 py-1.5 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                      min="0.001"
                      step="0.001"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Higher tips = higher priority
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="bg-gray-900/50 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <DollarSign className="w-4 h-4 text-orange-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-orange-400 mb-3">Launch Cost Breakdown</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Platform Fee:</span>
                  <span className="font-mono">0.01 SOL</span>
                </div>
                {selectedBundle && isUnlocked ? (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Bundle Buy ({selectedBundle.wallets.filter((w: any) => 
                      w.balance >= (initialBuyAmount + 0.002)
                    ).length} wallets):</span>
                    <span className="font-mono">
                      {(initialBuyAmount * selectedBundle.wallets.filter((w: any) => 
                        w.balance >= (initialBuyAmount + 0.002)
                      ).length).toFixed(3)} SOL
                    </span>
                  </div>
                ) : initialBuyAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Initial Buy:</span>
                    <span className="font-mono">{initialBuyAmount} SOL</span>
                  </div>
                )}
                {launchSettings.useJitoBundle && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Jito Tip:</span>
                    <span className="font-mono">{launchSettings.jitoTip} SOL</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-2">
                  <div className="flex justify-between font-medium">
                    <span>Total from Main Wallet:</span>
                    <span className="text-orange-400 font-mono">
                      {calculateTotalCost().toFixed(3)} SOL
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {launchSettings.solToRaise && (
          <div className="text-sm text-gray-500">
            Token will migrate to Raydium when bonding curve reaches {launchSettings.solToRaise} SOL
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !isConnected}
          className="w-full py-4 bg-gradient-to-r from-orange-600 to-yellow-600 rounded-xl font-semibold text-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-lg hover:shadow-orange-500/20"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Launching...</span>
            </>
          ) : (
            <>
              <Rocket className="w-5 h-5" />
              <span>Launch on LetsBonk.fun</span>
              <TrendingUp className="w-5 h-5" />
            </>
          )}
        </button>

        {!isConnected && (
          <p className="text-center text-sm text-yellow-400 mt-3 flex items-center justify-center space-x-2">
            <AlertCircle className="w-4 h-4" />
            <span>Please connect your wallet to launch</span>
          </p>
        )}
      </div>

      {/* Bundle Password Modal */}
      <Modal 
        isOpen={showBundlePasswordModal} 
        onClose={() => {
          setShowBundlePasswordModal(false);
          setBundlePassword('');
        }}
      >
        <h3 className="text-xl font-bold mb-4">Unlock Bundle</h3>
        <p className="text-sm text-gray-400 mb-4">
          Enter password to unlock "{selectedBundle?.name}" for auto-buying
        </p>
        
        <input
          type="password"
          value={bundlePassword}
          onChange={(e) => setBundlePassword(e.target.value)}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-orange-500"
          placeholder="Bundle password"
          onKeyPress={(e) => e.key === 'Enter' && unlockSelectedBundle()}
          autoFocus
        />
        
        <div className="flex gap-3">
          <button
            onClick={unlockSelectedBundle}
            className="flex-1 py-2 bg-gradient-to-r from-orange-600 to-yellow-600 rounded-lg font-medium hover:opacity-90 transition-colors"
          >
            Unlock
          </button>
          <button
            onClick={() => {
              setShowBundlePasswordModal(false);
              setBundlePassword('');
            }}
            className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BonkCreationForm;